(function () {
  const STORAGE_KEY = 'convert:size-quiz';
  const DEFAULT_STEPS = ['usual', 'measurements', 'preferredFit', 'result'];

  let coreReady = Boolean(window.ConvertSizeQuiz);
  let delegated = false;

  function whenCoreReady() {
    return new Promise((resolve) => {
      if (window.ConvertSizeQuiz) {
        resolve(window.ConvertSizeQuiz);
        return;
      }
      document.addEventListener('sizequiz:core-ready', () => resolve(window.ConvertSizeQuiz), { once: true });
    });
  }

  function readConfig(root) {
    const node = root.querySelector('[data-size-quiz-config]');
    if (!node) return null;
    try {
      const config = JSON.parse(node.textContent);
      if (config?.i18n) config.i18n = decodeStrings(config.i18n);
      return config;
    } catch {
      return null;
    }
  }

  // Shopify HTML-escapes translations that take arguments ("Height &amp; weight").
  function decodeStrings(value) {
    if (typeof value === 'string') return value.replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, '&');
    if (!value || typeof value !== 'object') return value;
    for (const key of Object.keys(value)) value[key] = decodeStrings(value[key]);
    return value;
  }

  function interpolate(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}|\[(\w+)\]/g, (match, liquidKey, bracketKey) => {
      const key = liquidKey || bracketKey;
      if (bracketKey && !(key in values)) return match;
      return values[key] ?? '';
    });
  }

  function sectionRoot(sectionId) {
    return document.querySelector(`[data-section-id="${sectionId}"]`) || document;
  }

  function showResult(sectionId, text) {
    const root = sectionRoot(sectionId);
    const span = root.querySelector('[data-size-quiz-result]');
    if (span) span.textContent = text;
    root.querySelectorAll('[data-size-quiz-redo-trigger]').forEach((button) => {
      button.hidden = !text;
    });
  }

  function readStored() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function writeStored(payload) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota */
    }
  }

  function normalizeSize(value) {
    return window.ConvertSizeQuiz?.normalizeSize(value) ?? String(value || '').trim().toLowerCase();
  }

  function getHeightIn(state) {
    const { ConvertSizeQuiz: core } = window;
    if (state.heightUnit === 'cm') return core.cmToIn(state.heightCm);
    return core.ftInToIn(state.heightFt, state.heightIn);
  }

  function getWeightLb(state) {
    const { ConvertSizeQuiz: core } = window;
    if (state.weightUnit === 'kg') return core.kgToLb(state.weightKg);
    return Number(state.weightLb);
  }

  function reasonMessage(result, config, size, usual = '') {
    const { reason, reasonDirection } = result;
    const fit = config.fit || 'true';
    let key = 'reason_measurements';
    if (reason === 'fit') {
      if (fit === 'small') key = 'reason_fit_small';
      else if (fit === 'large') key = 'reason_fit_large';
      else if (fit === 'boxy') key = 'reason_fit_boxy';
      else key = 'reason_measurements';
    } else if (reason === 'usual') {
      key = 'reason_usual';
    } else if (reason === 'measurements') {
      key = 'reason_measurements';
    } else {
      const dir = reasonDirection === 'down' ? 'down' : 'up';
      key = `reason_${reason}_${dir}`;
    }
    return interpolate(config.i18n[key] || config.i18n.reason_measurements, { size, usual });
  }

  function alternativeMessage(result, config) {
    if (!result?.alternative) return '';
    const key = result.alternative.direction === 'down' ? 'alternative_down' : 'alternative_up';
    return interpolate(config.i18n[key], { size: result.alternative.size });
  }

  function validateMeasurements(state) {
    const core = window.ConvertSizeQuiz;
    const height = state.heightUnit === 'cm' ? Number(state.heightCm) : getHeightIn(state);
    const weight = state.weightUnit === 'kg' ? Number(state.weightKg) : Number(state.weightLb);
    const heightUnit = state.heightUnit === 'cm' ? 'cm' : 'in';
    const weightUnit = state.weightUnit === 'kg' ? 'kg' : 'lb';
    const heightOk = core.isValidHeight(height, heightUnit);
    const weightOk = core.isValidWeight(weight, weightUnit);
    return { heightOk, weightOk };
  }

  function bindDialog(dialog) {
    if (dialog.dataset.sizeQuizBound === 'true') return;
    dialog.dataset.sizeQuizBound = 'true';

    const config = readConfig(dialog);
    if (!config?.sectionId) return;

    const sectionId = config.sectionId;
    const STEP_ORDER = Array.isArray(config.steps) && config.steps.length ? config.steps : DEFAULT_STEPS;
    const TOTAL_STEPS = STEP_ORDER.length;
    const dots = [...dialog.querySelectorAll('[data-dot]')];
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const flow = window.ConvertSizeQuiz.createStepFlow({
      steps: STEP_ORDER,
      reducedMotion: () => Boolean(reducedMotion?.matches),
      onChange: (index) => renderStep(index),
    });
    const track = dialog.querySelector('[data-size-quiz-track]');
    const progress = dialog.querySelector('[data-size-quiz-progress]');
    const stepLabel = dialog.querySelector('[data-size-quiz-step-label]');
    const backBtn = dialog.querySelector('[data-size-quiz-back]');
    const closeBtn = dialog.querySelector('[data-size-quiz-close]');
    const continueBtn = dialog.querySelector('[data-size-quiz-continue]');
    const selectBtn = dialog.querySelector('[data-size-quiz-select]');
    const addBtn = dialog.querySelector('[data-size-quiz-add]');
    const redoBtn = dialog.querySelector('[data-size-quiz-redo]');
    const resultLive = dialog.querySelector('[data-size-quiz-result-live]');
    const chartNoteEl = dialog.querySelector('[data-size-quiz-chart]');
    const productEl = dialog.querySelector('[data-size-quiz-product]');
    const productPriceEl = dialog.querySelector('[data-size-quiz-product-price]');

    const state = {
      step: 0,
      usualSize: '',
      heightUnit: 'imperial',
      heightFt: '',
      heightIn: '',
      heightCm: '',
      weightUnit: 'imperial',
      weightLb: '',
      weightKg: '',
      preferredFit: '',
      lastResult: null,
    };

    let opener = null;

    const visibleSteps = STEP_ORDER;

    function setStep(index) {
      flow.go(index);
    }

    function renderStep(index) {
      dialog.classList.remove('size-quiz--advancing');
      state.step = index;
      track.style.setProperty('--step', String(state.step));
      const stepName = STEP_ORDER[state.step];
      dialog.dataset.activeStep = stepName;
      dialog.querySelectorAll('[data-step]').forEach((section) => {
        section.inert = section.dataset.step !== stepName;
      });
      const position = visibleSteps.indexOf(stepName);
      const ratio = (position + 1) / visibleSteps.length;
      progress.style.setProperty('--progress', String(ratio));
      const onResult = state.step === TOTAL_STEPS - 1;
      if (stepLabel && config.i18n.step_of) {
        stepLabel.hidden = onResult;
        stepLabel.textContent = interpolate(config.i18n.step_of, {
          current: Math.min(position + 1, visibleSteps.length - 1),
          total: visibleSteps.length - 1,
        });
      }
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-done', dotIndex < state.step);
        dot.classList.toggle('is-current', dotIndex === state.step);
      });
      backBtn.hidden = state.step === 0;
      continueBtn.hidden = onResult || !flow.showContinue;
      if (addBtn) addBtn.hidden = !onResult || !config.showAddToCart;
      if (selectBtn) selectBtn.hidden = !onResult;
      if (redoBtn) redoBtn.hidden = !onResult;
      const heading = dialog.querySelector(`[data-step="${stepName}"] [data-step-heading]`);
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      const viewport = dialog.querySelector('.size-quiz__viewport');
      if (viewport) viewport.scrollTop = 0;
      if (stepName === 'result') renderResult();
    }

    function clearErrors() {
      dialog.querySelectorAll('[data-field-error]').forEach((el) => {
        el.hidden = true;
        el.textContent = '';
      });
      dialog.querySelectorAll('.size-quiz__field--error').forEach((el) => el.classList.remove('size-quiz__field--error'));
    }

    function showFieldError(field, message) {
      const wrap = dialog.querySelector(`[data-field="${field}"]`);
      if (!wrap) return;
      wrap.classList.add('size-quiz__field--error');
      const err = wrap.querySelector('[data-field-error]');
      if (err) {
        err.textContent = message;
        err.hidden = !message;
      }
    }

    function validateCurrentStep() {
      clearErrors();
      const stepName = STEP_ORDER[state.step];
      if (stepName === 'measurements') {
        const { heightOk, weightOk } = validateMeasurements(state);
        if (!heightOk) {
          showFieldError(
            'height',
            state.heightUnit === 'cm' ? config.i18n.error_height_metric : config.i18n.error_height_imperial
          );
        }
        if (!weightOk) {
          showFieldError(
            'weight',
            state.weightUnit === 'kg' ? config.i18n.error_weight_metric : config.i18n.error_weight_imperial
          );
        }
        return heightOk && weightOk;
      }
      if (stepName === 'usual') return Boolean(state.usualSize);
      if (stepName === 'preferredFit') return Boolean(state.preferredFit);
      return true;
    }

    function core() {
      return window.ConvertSizeQuiz;
    }

    // The tapped answer stays highlighted while the flow waits, then the track slides on.
    function advanceFrom(stepName) {
      if (flow.choose(stepName) && flow.pending) dialog.classList.add('size-quiz--advancing');
    }

    function collectInputs() {
      return {
        usualSize: state.usualSize === 'not_sure' ? '' : state.usualSize,
        heightIn: getHeightIn(state),
        weightLb: getWeightLb(state),
        preferredFit: state.preferredFit,
        category: config.category || 'general',
        fit: config.fit,
      };
    }

    function chartRangeForSize(size) {
      const core = window.ConvertSizeQuiz;
      const chart = core.parseChart(config.chart);
      const key = core.normalizeSize(size);
      const band = chart.find((row) => row.key === key);
      if (!band) return '';
      if (band.min <= 0) return `up to ${band.max}`;
      if (band.max >= 400) return `${band.min}+`;
      return `${band.min}–${band.max}`;
    }

    function readSelectedOptionsFromPage() {
      const root = sectionRoot(sectionId);
      const info = root.querySelector('product-info') || root.closest('product-info');
      const fieldsets = info ? [...info.querySelectorAll('fieldset[data-option-position]')] : [];
      return fieldsets.map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value ?? '');
    }

    function variantForSize(size) {
      const product = config.product;
      if (!product?.variants?.length) return null;
      const core = window.ConvertSizeQuiz;
      const targetKey = core.normalizeSize(size);
      const options = readSelectedOptionsFromPage();
      const sizeIndex = Number(product.sizeOptionIndex) || 0;
      if (!options.length) {
        const sized = product.variants.filter((variant) => core.normalizeSize(variant.options?.[sizeIndex]) === targetKey);
        return sized.find((variant) => variant.available) || sized[0] || null;
      }
      const trial = [...options];
      trial[sizeIndex] = size;
      const match = product.variants.find((variant) => {
        const vo = variant.options || [];
        return vo.every((value, index) => value === trial[index]);
      });
      if (match) return match;
      const sameSize = product.variants.filter((variant) => core.normalizeSize(variant.options?.[sizeIndex]) === targetKey);
      const score = (variant) =>
        (variant.options || []).reduce(
          (total, value, index) => total + (index !== sizeIndex && String(value).toLowerCase() === String(options[index] ?? '').toLowerCase() ? 2 : 0),
          variant.available ? 1 : 0
        );
      return sameSize.sort((a, b) => score(b) - score(a))[0] || null;
    }

    function updateResultActions(result, variant) {
      const size = result.size;
      if (selectBtn) selectBtn.textContent = interpolate(config.i18n.select, { size });
      if (!addBtn) return;
      if (!config.showAddToCart) {
        addBtn.hidden = true;
        return;
      }
      addBtn.hidden = false;
      addBtn.disabled = false;
      addBtn.dataset.variantId = variant?.id ? String(variant.id) : '';
      addBtn.dataset.size = size;
      if (variant?.available) {
        addBtn.textContent = interpolate(config.i18n.add_to_bag, { size });
        addBtn.classList.remove('button--secondary');
        addBtn.classList.add('button--primary');
      } else {
        const altSize = result.alternative?.size;
        const altVariant = altSize ? variantForSize(altSize) : null;
        if (altVariant?.available) {
          addBtn.textContent = interpolate(config.i18n.try_size, { size: altSize });
          addBtn.dataset.variantId = String(altVariant.id);
          addBtn.dataset.size = altSize;
        } else {
          addBtn.textContent = interpolate(config.i18n.sold_out, { size });
          addBtn.disabled = true;
        }
      }
    }

    function renderResult() {
      const core = window.ConvertSizeQuiz;
      const inputs = collectInputs();
      const result = core.recommendSize(inputs, config.chart, { offeredSizes: config.offeredSizes });
      state.lastResult = result;
      if (!result) return;

      const sizeEl = dialog.querySelector('[data-size-quiz-size]');
      const reasonEl = dialog.querySelector('[data-size-quiz-reason]');
      const altEl = dialog.querySelector('[data-size-quiz-alternative]');

      if (sizeEl) sizeEl.textContent = result.size;
      if (reasonEl) {
        reasonEl.textContent = reasonMessage(result, config, result.size, state.usualSize === 'not_sure' ? '' : state.usualSize);
        reasonEl.hidden = !reasonEl.textContent;
      }
      if (altEl) {
        const alt = alternativeMessage(result, config);
        altEl.textContent = alt;
        altEl.hidden = !alt;
      }
      if (chartNoteEl) {
        const range = chartRangeForSize(result.size);
        chartNoteEl.textContent = range
          ? interpolate(config.i18n.chart_weight, { size: result.size, range })
          : '';
        chartNoteEl.hidden = !range;
      }
      const variant = variantForSize(result.size);
      if (productEl) productEl.hidden = !config.product?.title;
      if (productPriceEl && variant) {
        productPriceEl.textContent = window.Theme?.formatMoney
          ? window.Theme.formatMoney(variant.price)
          : `$${(variant.price / 100).toFixed(2)}`;
      }
      updateResultActions(result, variant);
      window.Theme?.track?.('size_quiz_completed', {
        size: result.size,
        productId: config.product?.id ?? null,
        productTitle: config.product?.title || '',
        variantId: variant?.id ?? null,
      });
      window.Theme?.ss?.('feat', 'size_finder_result', { size: result.size, variant: variant?.id ?? null });
      if (resultLive) {
        resultLive.textContent = interpolate(config.i18n.your_size, { size: result.size });
      }
    }

    function applySelection(size) {
      showResult(sectionId, interpolate(config.i18n.your_size, { size }));
      writeStored({ size, inputs: collectInputs(), at: Date.now() });
      document.dispatchEvent(
        new CustomEvent('sizequiz:select', { detail: { size, sectionId } })
      );
    }

    function restoreStoredResult() {
      const stored = readStored();
      if (!stored?.size) return;
      const key = normalizeSize(stored.size);
      const offered = (config.offeredSizes || []).some((label) => normalizeSize(label) === key);
      if (!offered) return;
      showResult(sectionId, interpolate(config.i18n.your_size, { size: stored.size }));
    }

    function syncInputsFromDom() {
      dialog.querySelectorAll('[data-size-quiz-input]').forEach((input) => {
        const field = input.dataset.sizeQuizInput;
        if (field) state[field] = input.value;
      });
      const height = dialog.querySelector('[data-size-quiz-pick="heightImperial"]')?.value || '';
      state.heightFt = height ? String(Math.floor(Number(height) / 12)) : '';
      state.heightIn = height ? String(Number(height) % 12) : '';
    }

    function pick(name) {
      return dialog.querySelector(`[data-size-quiz-pick="${name}"], [data-size-quiz-input="${name}"]`);
    }

    // Moves a pick to the nearest option in the other unit so switching units keeps the answer.
    function carryPick(fromName, toName, convert) {
      const from = pick(fromName);
      const to = pick(toName);
      if (!from || !to || from.value === '') return;
      const target = convert(Number(from.value));
      let best = null;
      [...to.options].forEach((option) => {
        if (option.value === '') return;
        if (!best || Math.abs(Number(option.value) - target) < Math.abs(Number(best.value) - target)) best = option;
      });
      if (best) to.value = best.value;
    }

    function clearPicks() {
      dialog.querySelectorAll('select[data-size-quiz-input], select[data-size-quiz-pick]').forEach((select) => {
        select.selectedIndex = 0;
      });
    }

    function measurementsAnswered() {
      const height = state.heightUnit === 'cm' ? state.heightCm : state.heightFt;
      const weight = state.weightUnit === 'kg' ? state.weightKg : state.weightLb;
      if (height === '' || weight === '') return false;
      const { heightOk, weightOk } = validateMeasurements(state);
      return heightOk && weightOk;
    }

    function resetQuiz() {
      state.usualSize = '';
      state.preferredFit = '';
      state.lastResult = null;
      clearPicks();
      dialog.querySelectorAll('[data-usual-size], [data-choice]').forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
      syncInputsFromDom();
      clearErrors();
      flow.reset();
    }

    dialog.querySelectorAll('[data-usual-size]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.usualSize;
        state.usualSize = value;
        dialog.querySelectorAll('[data-usual-size]').forEach((peer) => {
          peer.setAttribute('aria-pressed', peer === btn ? 'true' : 'false');
        });
        advanceFrom('usual');
      });
    });

    dialog.querySelectorAll('[data-choice]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = btn.dataset.choice;
        state[name] = btn.dataset.value || '';
        dialog.querySelectorAll(`[data-choice="${name}"]`).forEach((peer) => {
          peer.setAttribute('aria-pressed', peer === btn ? 'true' : 'false');
        });
        advanceFrom(name);
      });
    });

    dialog.querySelectorAll('[data-unit-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.dataset.unitGroup;
        const value = btn.dataset.unitToggle;
        const peers = dialog.querySelectorAll(`[data-unit-group="${group}"]`);
        peers.forEach((peer) => {
          const active = peer === btn;
          peer.setAttribute('aria-pressed', active ? 'true' : 'false');
          peer.classList.toggle('size-quiz__unit--active', active);
        });
        if (group === 'height') {
          if (value === 'metric') carryPick('heightImperial', 'heightCm', core().inToCm);
          else carryPick('heightCm', 'heightImperial', core().cmToIn);
          state.heightUnit = value === 'metric' ? 'cm' : 'imperial';
          dialog.querySelector('[data-height-imperial]').hidden = value !== 'imperial';
          dialog.querySelector('[data-height-metric]').hidden = value !== 'metric';
        } else {
          if (value === 'metric') carryPick('weightLb', 'weightKg', core().lbToKg);
          else carryPick('weightKg', 'weightLb', core().kgToLb);
          state.weightUnit = value === 'metric' ? 'kg' : 'imperial';
          dialog.querySelector('[data-weight-imperial]').hidden = value !== 'imperial';
          dialog.querySelector('[data-weight-metric]').hidden = value !== 'metric';
        }
        syncInputsFromDom();
        clearErrors();
      });
    });

    // Height and weight move on once both are picked.
    dialog.querySelectorAll('select[data-size-quiz-input], select[data-size-quiz-pick]').forEach((select) => {
      select.addEventListener('change', () => {
        syncInputsFromDom();
        clearErrors();
        const stepName = select.closest('[data-step]')?.dataset.step;
        if (stepName !== STEP_ORDER[state.step]) return;
        if (stepName === 'measurements' && measurementsAnswered()) advanceFrom('measurements');
      });
    });

    continueBtn?.addEventListener('click', () => {
      syncInputsFromDom();
      if (!validateCurrentStep()) return;
      setStep(state.step + 1);
    });

    backBtn?.addEventListener('click', () => setStep(state.step - 1));

    closeBtn?.addEventListener('click', () => dialog.close());

    selectBtn?.addEventListener('click', () => {
      if (!state.lastResult?.size) return;
      applySelection(state.lastResult.size);
      dialog.close();
    });

    addBtn?.addEventListener('click', async () => {
      const variantId = Number(addBtn.dataset.variantId);
      const size = addBtn.dataset.size || state.lastResult?.size;
      if (!variantId || !size || addBtn.disabled) return;
      applySelection(size);
      dialog.close();
      try {
        if (window.Theme?.cart?.add) {
          const pagePlan = sectionRoot(sectionId).querySelector('[name="selling_plan"]:checked')?.value;
          const item = pagePlan
            ? { id: variantId, quantity: 1, selling_plan: Number(pagePlan) }
            : window.Theme.withSellingPlan({ id: variantId, quantity: 1 }, config.product);
          await window.Theme.cart.add([item], { source: 'add' });
        }
      } catch {
        /* Theme.cart surfaces errors */
      }
    });

    redoBtn?.addEventListener('click', () => resetQuiz());

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      dialog.close();
    });

    // Tapping the backdrop closes the quiz; the press has to start outside too, so a drag out of a field doesn't.
    const outsideCard = (event) => {
      if (event.target !== dialog) return false;
      const rect = dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    };
    let pressedOutside = false;
    dialog.addEventListener('pointerdown', (event) => {
      pressedOutside = outsideCard(event);
    });
    dialog.addEventListener('click', (event) => {
      if (pressedOutside && outsideCard(event)) dialog.close();
      pressedOutside = false;
    });

    dialog.addEventListener('close', () => {
      if (opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.target.closest('textarea')) return;
      if (event.target.closest('button')) return;
      if (state.step === TOTAL_STEPS - 1) return;
      event.preventDefault();
      if (validateCurrentStep()) setStep(state.step + 1);
    });

    dialog.openQuiz = (trigger) => {
      opener = trigger || null;
      // Lazy images in the off-screen result slide never start loading.
      dialog.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
      if (!dialog.open) {
        dialog.showModal();
        window.Theme?.ss?.('feat', 'size_finder_open', {});
      }
      resetQuiz();
    };

    restoreStoredResult();
    setStep(0);
  }

  function init() {
    document.querySelectorAll('dialog[data-size-quiz]').forEach(bindDialog);
  }

  function bindDelegation() {
    if (delegated) return;
    delegated = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-size-quiz-open]');
      if (!trigger) return;
      const sectionId = trigger.dataset.sectionId;
      if (!sectionId) return;
      const dialog = document.querySelector(`dialog[data-size-quiz][data-section-id="${sectionId}"]`);
      if (!dialog?.openQuiz) return;
      event.preventDefault();
      dialog.openQuiz(trigger);
    });
  }

  function boot() {
    bindDelegation();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => whenCoreReady().then(boot));
  } else {
    whenCoreReady().then(boot);
  }

  document.addEventListener('shopify:section:load', () => whenCoreReady().then(init));
})();
